import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppSelector } from 'hooks';
import { InspectionImages } from 'models/response/InspectionResponse';
import { useLazyFetchImagesQuery, usePatchCarImageMutation } from 'services/modules/inspection';
import { setCurrentImage } from 'store/inspection';
import { setErrorToast } from 'store/toast';
import { Button, Footer, Header, Image, LoadingLottie, Spinner, TakeImage } from 'ui';
import { fileDocumentType, mediaType } from 'ui/TakeImage';

import { routes } from 'utils/routeConfig';

import { DefaultCarSvg, FileSvg, PlaySvg } from '../../svg';
import * as S from '../CarDetail/CarDetailElements';

import {
  ButtonIcon,
  Container,
  Content,
  EditButton,
  ImageWrapper,
  ItemContainer,
  Label,
  PicturesWrapper,
  StatusBar,
  StatusLabel,
  UploadImage,
} from './CarPicturesElements';

export const CarPictures = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const params = useParams();
  const dispatch = useDispatch();
  const type = params.segment_slug;
  const [isOpenTakeImage, onToggleIsOpenTakeImage] = useState(false);

  const { currentCar } = useAppSelector((state) => state.inspection);
  const [imagesList, setImagesList] = useState<InspectionImages[]>([]);
  const [selectedImage, setSelectedImage] = useState<InspectionImages | null>(null);
  const [
    getImagesList,
    { data: dataImagesList, isLoading: isLoadingImagesList, isSuccess: isSuccessImagesList },
  ] = useLazyFetchImagesQuery();

  useEffect(() => {
    if (params.slug) {
      getImagesList(params.slug);
    }
  }, []);

  useEffect(() => {
    if (isSuccessImagesList && dataImagesList) {
      setImagesList(
        dataImagesList.filter((item) =>
          'segment_slug' in params
            ? item.segment.slug === `inspection_documents`
            : item.segment.slug !== 'inspection_documents',
        ),
      );
    }
  }, [dataImagesList, isSuccessImagesList]);

  const [updateImage, { data: dataImage, isLoading: isLoadingImage, isSuccess: isSuccessImage }] =
    usePatchCarImageMutation();

  const onChooseImage = (file: File) => {
    const slug = params.slug;
    const template_slug = selectedImage?.slug;
    if (slug && template_slug) {
      const body = new FormData();
      body.append('image', file, file.name);
      body.append('template_slug', template_slug);
      updateImage({ slug, body });
    }
  };

  useEffect(() => {
    if (isSuccessImage && dataImage) {
      const newImages = JSON.parse(JSON.stringify(imagesList));
      const index = imagesList.findIndex((v) => v.slug === selectedImage?.slug);
      if (index !== -1) {
        newImages[index].image = dataImage.uploaded_image;
        setImagesList(newImages);
      }
    }
  }, [dataImage, isSuccessImage]);

  const finish_car_picture = () => {
    if ('segment_slug' in params && imagesList.find((v) => v.image === null && !v.can_skip)) {
      dispatch(setErrorToast({ title: '', message: t('car_inspection.error_complete_document') }));
    } else if (imagesList.find((v) => v.image === null && !v.can_skip)) {
      dispatch(setErrorToast({ title: '', message: t('car_inspection.error_complete_picture') }));
    } else {
      navigate(-1);
    }
  };

  const handleUploadImage = () => {
    onToggleIsOpenTakeImage(true);
    setTimeout(() => {
      onToggleIsOpenTakeImage(false);
    }, 0);
  };

  return (
    <Container>
      <Header
        title={
          currentCar
            ? currentCar?.model_full_name.make +
              ' ' +
              currentCar?.model_full_name.model +
              ' ' +
              currentCar?.model_full_name.year
            : ''
        }
        hasBack={true}
      />
      <Content>
        <StatusBar>
          <StatusLabel>
            {type !== 'documents'
              ? t('car_inspection.pictures_of_car')
              : t('car_inspection.documents')}
          </StatusLabel>
          {currentCar && (
            <S.StatusLabel>{`${t('inspection.car_number')}: ${
              currentCar?.car_number
            }`}</S.StatusLabel>
          )}
        </StatusBar>
        {isLoadingImagesList ? (
          <LoadingLottie />
        ) : (
          <PicturesWrapper>
            {imagesList.map((item, index) => (
              <ItemContainer key={item.slug}>
                <ImageWrapper
                  onClick={() => {
                    if (item.image) {
                      navigate(
                        routes.imageViewer
                          .replace(':slug', `${params.slug}`)
                          .replace(
                            ':index',
                            `${index + 1}-${imagesList.length}${
                              (type === 'documents' && '?type=documents') || ''
                            }`,
                          ),
                      );
                      dispatch(setCurrentImage(item));
                    } else {
                      handleUploadImage();
                      setSelectedImage(item);
                    }
                  }}
                >
                  {(!isLoadingImage || selectedImage?.slug !== item.slug) && (
                    <>
                      {item.image ? (
                        <>
                          {item.image.type.includes('image') ? (
                            <Image src={item.image.url} height={120} width={160} />
                          ) : item.image.type.includes('video') ? (
                            <>
                              <DefaultCarSvg className={'tw-w-[120px] tw-h-[60px]'} />
                              <PlaySvg className={'tw-absolute  tw-w-[40px] tw-h-[40px]'} />
                            </>
                          ) : (
                            <div className={'tw-flex tw-flex-col tw-items-center tw-gap-1'}>
                              <FileSvg className={'tw-fill-primaryGreen500'} />
                              <p
                                className={
                                  'tw-text-primaryGreen500 tw-uppercase tw-text-sm -tw-mb-1'
                                }
                              >
                                {item.image.url.split('.')?.[1]}
                              </p>
                            </div>
                          )}
                          <EditButton
                            className={'icon-Left-icon'}
                            onClick={(event) => {
                              event.stopPropagation();
                              handleUploadImage();
                              setSelectedImage(item);
                            }}
                          />
                        </>
                      ) : (
                        <UploadImage className={'icon-u_image-upload'} />
                      )}
                    </>
                  )}
                  {item?.can_skip && (
                    <span
                      className={
                        'tw-text-gray50 tw-text-[12px] tw-absolute tw-bottom-3 tw-left-0 tw-bg-orange500 tw-px-1 tw-rounded-e-2xl'
                      }
                    >
                      {'Optional'}
                    </span>
                  )}
                </ImageWrapper>
                <Label>{item.title}</Label>
              </ItemContainer>
            ))}
            {isLoadingImage && (
              <div
                className={
                  'tw-absolute tw-z-30 tw-bg-black tw-bg-opacity-60 tw-flex tw-items-center tw-justify-center tw-left-0 tw-right-0 tw-bottom-0 tw-top-0'
                }
              >
                <Spinner isLoading={true} />
              </div>
            )}
          </PicturesWrapper>
        )}
      </Content>
      <Footer slug={params.slug}>
        <Button onClick={finish_car_picture} label={t('footer.finish_car_pictures')} size={'large'}>
          <ButtonIcon className={'icon-Right-Icon'} />
        </Button>
      </Footer>
      <TakeImage
        onChooseImage={onChooseImage}
        isRotate={type !== 'documents'}
        allowedTypes={(type !== 'documents' ? mediaType : fileDocumentType) || undefined}
        isOpenTakeImage={isOpenTakeImage}
      />
    </Container>
  );
};
